/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Drawer, TableProps } from 'antd';
import { useMemo, useState } from 'react';

import { Node, NodeOperatingSystem } from '@/client';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedNodesQuery } from '@/hooks/query/usePaginatedNodesQuery';
import { usePaginatedTagsQuery } from '@/hooks/query/usePaginatedTagsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { NodesColumns } from '@/pages/Nodes/getNodesColumns';
import { NodesTable } from '@/pages/Nodes/NodesTable';
import { Button } from '@/primitives/Button';
import { Card } from '@/primitives/Card';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import './AddNodesToGroupDrawer.css';

export type AddNodesToGroupDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (nodeIds: string[]) => void;
  operatingSystem: NodeOperatingSystem;
  isSubmitting?: boolean;
};

const visibleNodeColumns: NodesColumns[] = [
  'hostname',
  'tags',
  'connectivityState',
  'operatingSystem',
  'lastSeen',
  'appliedConfiguration',
];

export function AddNodesToGroupDrawer({
  open,
  onClose,
  onSubmit,
  operatingSystem,
  isSubmitting,
}: AddNodesToGroupDrawerProps) {
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const { t } = useMimicTranslation('fleets');

  const nodesQuery = usePaginatedNodesQuery({
    params: {
      filters: { operatingSystem, hasNodeGroup: false },
    },
  });

  const tagsQuery = usePaginatedTagsQuery({ params: { page: 1, pageSize: 100 } });

  const handleClose = () => {
    setSelectedNodeIds([]);
    onClose();
  };

  const handleSubmit = () => {
    if (selectedNodeIds.length === 0) return;
    onSubmit(selectedNodeIds);
    setSelectedNodeIds([]);
  };

  const getNodeConfigurationPath = (nodeConfigId: string) => `/tenants/${tenantId}/node-configs/${nodeConfigId}`;

  const rowSelection: TableProps<Node>['rowSelection'] = useMemo(
    () => ({
      type: 'checkbox',
      preserveSelectedRowKeys: true,
      selectedRowKeys: selectedNodeIds,
      onChange: (_selectedKeys: React.Key[], selectedRows: Node[]) => {
        setSelectedNodeIds(selectedRows.map((node) => node.id));
      },
      getCheckboxProps: (node: Node) => ({
        name: node.name,
      }),
    }),
    [selectedNodeIds],
  );

  const footer = (
    <div className="add-nodes-drawer-footer">
      <div className="right-buttons">
        <Button dataTestId="cancel-add-nodes" type="link" onClick={handleClose} disabled={isSubmitting}>
          {t('cancel')}
        </Button>
        <Button
          dataTestId="confirm-add-nodes"
          type="primary"
          onClick={handleSubmit}
          disabled={selectedNodeIds.length === 0 || isSubmitting}
          loading={isSubmitting}
        >
          {selectedNodeIds.length > 0
            ? t('addNodesDrawer.addNodesCount', { count: selectedNodeIds.length })
            : t('addNodesDrawer.addNodes')}
          <CheckOutlined />
        </Button>
      </div>
    </div>
  );

  return (
    <Drawer
      rootClassName="add-nodes-drawer"
      placement="top"
      title={t('addNodesDrawer.title')}
      open={open}
      height="100vh"
      onClose={handleClose}
      closeIcon={null}
      extra={<CloseButton onClick={handleClose} />}
      footer={footer}
    >
      <Card dataTestId="add-nodes-select" padding="sm" bordered style={{ width: '100%' }}>
        <Text type="subtle" style={{ marginBottom: 16, display: 'block' }}>
          {operatingSystem === 'windows'
            ? t('addNodesDrawer.selectNodesWindows')
            : t('addNodesDrawer.selectNodesLinux')}
        </Text>
        <QueryWrapper queryResult={nodesQuery}>
          {(nodesData) => (
            <QueryWrapper queryResult={tagsQuery}>
              {(tagsData) => (
                <NodesTable
                  tenantID={tenantId!}
                  nodesData={nodesData}
                  onParamsChange={nodesQuery.updateQueryParams}
                  columns={visibleNodeColumns}
                  tags={tagsData.data}
                  getNodeConfigurationPath={getNodeConfigurationPath}
                  rowSelection={rowSelection}
                  selectedNodeIds={selectedNodeIds}
                  hideFilters
                />
              )}
            </QueryWrapper>
          )}
        </QueryWrapper>
      </Card>
    </Drawer>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <Button dataTestId="close-add-nodes-drawer" type="text" onClick={onClick}>
      <CloseOutlined />
    </Button>
  );
}
